import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { HiOutlineMail, HiOutlineLockClosed } from 'react-icons/hi';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { useTranslation } from 'react-i18next';
import { systemAPI, settingsAPI } from '../services/api';

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [version, setVersion] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await systemAPI.getVersion();
        setVersion(response.data.version);
      } catch (error) {
        console.error('Error fetching version:', error);
        setVersion('1.4.1'); // Fallback
      }
    };

    const fetchRegistrationStatus = async () => {
      try {
        const response = await settingsAPI.getRegistrationStatus();
        setRegistrationEnabled(response.data.enabled);
      } catch (error) {
        console.error('Error fetching registration status:', error);
        setRegistrationEnabled(true); // Default to enabled on error
      }
    };

    fetchVersion();
    fetchRegistrationStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      // FIX NUCLEAR FORÇADO: Tradução direta para erro de login
      const errorDetail = error.response?.data?.detail;
      
      console.log('🔍 [DEBUG] Error Detail:', errorDetail);
      console.log('🔍 [DEBUG] Translation Key:', 'errors.invalidCredentials');
      console.log('🔍 [DEBUG] Resultado:', t('errors.invalidCredentials'));

      if (errorDetail === 'Invalid email or password' || 
          errorDetail === 'Email ou senha incorretos' ||
          errorDetail === 'TEST_ERROR_V2_BACKEND_UPDATED') {
        // Tradução direta aqui para contornar o i18next
        toast.error(t('errors.invalidCredentials'));
      } else {
        // Outros erros usam o handler padrão
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
      
      // Reset password field after error
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <h1>My Series</h1>
          <p>Movies & Series Tracker</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('common.email')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineMail
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="email"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('profile.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('common.password')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineLockClosed
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="password"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('common.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '8px' }}
            disabled={loading}
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          {registrationEnabled && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                {t('auth.dontHaveAccount')}{' '}
              </span>
              <Link
                to="/register"
                style={{
                  color: 'var(--primary)',
                  fontSize: '14px',
                  textDecoration: 'underline',
                }}
              >
                {t('auth.createAccount')}
              </Link>
            </div>
          )}
        </form>

        <p
          style={{
            textAlign: 'center',
            marginTop: '24px',
            fontSize: '12px',
            color: '#6b7280',
          }}
        >
          {t('auth.copyright')}
        </p>
        <p
          style={{
            textAlign: 'center',
            marginTop: '8px',
            fontSize: '11px',
            color: '#9ca3af',
          }}
        >
          {t('common.version')} {version}
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
}

export default Login;
